import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import AddFundsErrorSection from './add-funds-error-section';
import FundingAmountIconToolTip from './funding-amount-icon-tooltip';
import messages from './message';
import style from './style.module.less';
import DataDrivenLogo from '../../../../../../components/data-driven-logo';
import GetInsuranceProviderDisplayName from '../../../../../../components/data-driven-logo/get-insurance-provider-display-name';
import FormattedCurrency from '../../../../../../components/formatted-currency/container';
import ModalPortal from '../../../../../../components/modal-portal';
import useInsuranceProvider from '../../hooks/use-insurance-provider';
import usePurchaseInsurance from '../../hooks/use-purchase-insurance';
import useSellerInsuranceProvider from '../../hooks/use-seller-insurance-provider';
import { useTrackEvent, PageEvents } from '../../hooks/use-track-event';

const isValidNumber = (value) => {
  return !Number.isNaN(Number(value));
};

const canAddFunds = (fundingAmount, loading, insuranceProvider) => {
  if (!isValidNumber(fundingAmount)) {
    return false;
  }
  return !loading && Number(fundingAmount) >= insuranceProvider?.minimumFundingAmount?.value;
};

const calculateResultingFundingAmount = (fundingAmount, sellerInsuranceProvider) => {
  const additionalValue = isValidNumber(fundingAmount) ? Number(fundingAmount) : 0;
  return {
    code: sellerInsuranceProvider?.balance?.code,
    value: (sellerInsuranceProvider?.balance?.value || 0) + additionalValue,
  };
};

const AddFundsModal = ({ sellerInsuranceProviderId, onClose, onSuccess }) => {
  const sellerInsuranceProvider = useSellerInsuranceProvider(sellerInsuranceProviderId);
  const insuranceProvider = useInsuranceProvider(sellerInsuranceProvider?.insuranceProviderId);

  const [fundingAmount, setFundingAmount] = useState(insuranceProvider?.minimumFundingAmount?.value);

  const { loading, errors, purchaseInsurance } = usePurchaseInsurance();

  const validateAndSetFundingAmount = useCallback(
    (proposedFundingAmount) => {
      if (!isValidNumber(proposedFundingAmount)) {
        return;
      }
      setFundingAmount(proposedFundingAmount);
    },
    [setFundingAmount],
  );

  const trackClickEvent = useTrackEvent(PageEvents.CONFIRM_FUND_SHIPSURANCE_CLICK);

  const addFunds = useCallback(async () => {
    const amountAdded = Number(fundingAmount);
    const currencyCode = sellerInsuranceProvider?.balance?.code;

    trackClickEvent({ currencyCode, fundAmount: fundingAmount });
    const result = await purchaseInsurance({
      value: amountAdded,
      code: currencyCode,
    });
    if (result) {
      onSuccess({ result, amountAdded, currencyCode });
    }
  }, [fundingAmount, onSuccess, purchaseInsurance, sellerInsuranceProvider?.balance?.code, trackClickEvent]);

  const resultingAmount = calculateResultingFundingAmount(fundingAmount, sellerInsuranceProvider);

  return (
    <ModalPortal containerId='insurance-add-funds-modal' onClose={onClose}>
      <Modal className={style['modal-container']}>
        <ModalHeader closeModal={onClose}>
          <FormattedMessage {...messages.addFundsHeader} />
        </ModalHeader>
        <ModalBody>
          {errors?.length !== 0 && <AddFundsErrorSection errors={errors} minimumFundingAmount={insuranceProvider?.minimumFundingAmount} />}
          <div className={style.grid}>
            <div className={style.label}>
              <FormattedMessage {...messages.insuranceProviderLabel} />
            </div>
            <div className={style['icon-container']}>
              <div className={style.icon}>
                <DataDrivenLogo insuranceProviderId={insuranceProvider.insuranceProviderId} />
              </div>
              <div>
                <FormattedMessage {...GetInsuranceProviderDisplayName(insuranceProvider.insuranceProviderId)} />
              </div>
            </div>
            <div className={style.label}>
              <FormattedMessage {...messages.currentBalanceLabel} />
            </div>
            <FormattedCurrency currencyCode={sellerInsuranceProvider?.balance?.code} value={sellerInsuranceProvider?.balance?.value} />
            <div className={style.label}>
              <FormattedMessage {...messages.fundingAmountLabel} />
              <FundingAmountIconToolTip insuranceProviderId={sellerInsuranceProvider?.insuranceProviderId} />
            </div>
            <CurrencyInput
              className={style['funding-amount-input']}
              currencyCode={sellerInsuranceProvider?.balance?.code}
              disallowNegative
              onChange={validateAndSetFundingAmount}
              value={fundingAmount}
            />
            <div className={style.label}>
              <FormattedMessage {...messages.resultingAmountLabel} />
            </div>
            <FormattedCurrency currencyCode={resultingAmount.code} value={resultingAmount.value} />
          </div>
        </ModalBody>
        <ModalFooter className={style['modal-footer']}>
          <Button onClick={onClose} theme='tertiary'>
            <FormattedMessage {...messages.cancelButton} />
          </Button>
          <Button disabled={!canAddFunds(fundingAmount, loading, insuranceProvider)} onClick={addFunds} theme='primary'>
            <FormattedMessage {...messages.addFundsButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

AddFundsModal.propTypes = {
  sellerInsuranceProviderId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddFundsModal;
